import {useEffect, useState} from 'react';
import handRaisedWithoutWaving from 'assets/images/characters/CarolinaMariadeJesus/hand-raised-without-waving.webp';
import speakingDefault from 'assets/images/characters/CarolinaMariadeJesus/speaking-default.webp';
import speakingPoint from 'assets/images/characters/CarolinaMariadeJesus/speaking-point.webp';
import {Indicators, ShownStat} from 'utils/types/newReviewed';
import useNewReviewed from 'features/useNewReviewed';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useNavigate} from 'react-router-dom';
import {Button, Modal} from 'letrus-ui';
import styles from './CommentsLoadingModal.module.scss';

export interface CommentsLoadingModalProps {
  id: string;
  onClose(): void;
  indicators: Indicators | null;
  reviewId?: number;
  failedToConnect?: boolean;
  shownStats: ShownStat[];
}

export interface Comments {
  currentComment: number;
  currentImage: number;
}

export interface CurrentComment {
  text: string;
  isQuestion: boolean;
  correctAnswer?: string;
  incorrectAnswer?: string;
}

const images = [handRaisedWithoutWaving, speakingDefault, speakingPoint];

const COMMENT_ANIMATION_SPEED_IN_MILLISECONDS = 30;
const MAIN_BUTTON_SUSPENSION_TIME_IN_MILLISECONDS = 3000;

function CommentsLoadingModal({
  id,
  onClose,
  indicators,
  reviewId,
  shownStats,
  failedToConnect,
}: CommentsLoadingModalProps): JSX.Element {
  const [commentsReviewed, setCommentsReviewed] = useState<Comments>({
    currentComment: 0,
    currentImage: 0,
  });
  const [commentAnimation, setCommentAnimation] = useState('');
  const [stopAnimation, setStopAnimation] = useState(false);
  const [questionAnswer, setQuestionAnswer] = useState<string | undefined>('');
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);
  const [hasExceededReviewWaitTime, setHasExceededReviewWaitTime] =
    useState(false);

  const {updateCommentsReviewed, getButtonText, getCurrentComments} =
    useNewReviewed({images});
  const {trackEvent} = useTrackEvent();
  const navigate = useNavigate();

  const comments = getCurrentComments(
    indicators ?? {},
    shownStats,
  ).currentComments;

  const currentComment = comments[commentsReviewed.currentComment];

  const isLastComment = commentsReviewed.currentComment === comments.length - 1;

  const isLastCommentPendingReview =
    (isLastComment && hasExceededReviewWaitTime && !reviewId) ||
    failedToConnect;

  useEffect(() => {
    trackEvent('step1_feedback_screen_view');
  }, []);

  useEffect(() => {
    setCommentAnimation('');

    const fullComment = currentComment.text;

    let currentCharacter = 0;

    const interval = setInterval(() => {
      setCommentAnimation(
        (prevText) => prevText + fullComment[currentCharacter],
      );

      currentCharacter += 1;

      if (currentCharacter === fullComment.length) {
        clearInterval(interval);
      }
    }, COMMENT_ANIMATION_SPEED_IN_MILLISECONDS);

    setTimeout(() => {
      setIsButtonEnabled(true);
    }, MAIN_BUTTON_SUSPENSION_TIME_IN_MILLISECONDS);

    if (isLastComment) {
      setTimeout(() => {
        setHasExceededReviewWaitTime(true);
      }, MAIN_BUTTON_SUSPENSION_TIME_IN_MILLISECONDS);
    }

    return () => clearInterval(interval);
  }, [commentsReviewed.currentComment]);

  // this function is provisional, we'll probably change it later to calculate according to the reviewed steps we'll receive from the back
  function calculateReviewedProgress() {
    const reviewedProgress =
      ((commentsReviewed.currentComment + 1) / comments.length) * 100;

    if (!reviewId && Math.round(reviewedProgress) >= 100) {
      return 99;
    }

    return reviewedProgress;
  }

  function getContentComment() {
    if (isLastCommentPendingReview) {
      return 'Estamos processando sua correção. Está demorando mais do que o normal, volte aqui daqui a pouco que em breve seu texto será corrigido. Desculpe o transtorno.';
    }

    return stopAnimation ? currentComment.text : commentAnimation;
  }

  return (
    <Modal id={id}>
      <section className={styles.container}>
        <h3>
          Descubra os segredos dos textos nota máxima enquanto processamos o
          seu! Adivinhe agora!
        </h3>

        <article className={styles.commentContainer}>
          <img src={images[commentsReviewed.currentImage]} alt="Avatar" />

          <button
            className={`${styles.currentComment} ${
              isLastCommentPendingReview ? styles.commentPendingReview : ''
            }`}
            type="button"
            onClick={() => setStopAnimation(true)}
          >
            {getContentComment()}
          </button>

          {currentComment.isQuestion && !questionAnswer && (
            <div className={styles.answersButtons}>
              <Button
                variant="secondary"
                disabled={!isButtonEnabled}
                onClick={() => {
                  setQuestionAnswer(currentComment.correctAnswer);
                }}
              >
                {currentComment.correctAnswer}
              </Button>

              <Button
                variant="secondary"
                disabled={!isButtonEnabled}
                onClick={() => {
                  setQuestionAnswer(currentComment.incorrectAnswer);
                }}
              >
                {currentComment.incorrectAnswer}
              </Button>
            </div>
          )}

          {(currentComment.isQuestion === false || questionAnswer) && (
            <section className={styles.buttonsContainer}>
              <Button
                variant="secondary"
                disabled={
                  isLastComment ? !reviewId : !isButtonEnabled || !indicators
                }
                loading={
                  isLastComment ? !reviewId : !isButtonEnabled || !indicators
                }
                onClick={() => {
                  if (commentsReviewed.currentComment === comments.length - 1) {
                    onClose();
                    return;
                  }

                  updateCommentsReviewed(setCommentsReviewed);
                  setIsButtonEnabled(false);
                  setStopAnimation(false);
                  setQuestionAnswer('');
                }}
              >
                {getButtonText(
                  commentsReviewed.currentComment,
                  comments.length,
                )}
              </Button>

              {isLastCommentPendingReview && (
                <Button variant="secondary" onClick={() => navigate('/')}>
                  Sair
                </Button>
              )}
            </section>
          )}

          {questionAnswer && (
            <aside
              className={`${styles.questionFeedback} ${
                currentComment.correctAnswer === questionAnswer
                  ? styles.correctAnswer
                  : styles.incorrectAnswer
              }`}
            >
              <p>
                {getCurrentComments(
                  indicators ?? {},
                  shownStats,
                ).getQuestionFeedback(questionAnswer)}
              </p>
              <strong>
                {currentComment.correctAnswer === questionAnswer
                  ? 'Correto'
                  : 'Incorreto'}
              </strong>
            </aside>
          )}
        </article>
      </section>

      <section className={styles.progressBarContainer}>
        <div
          className={`${styles.progressBar} ${
            styles[`PercentProgress${Math.round(calculateReviewedProgress())}`]
          }`}
          style={{width: `${calculateReviewedProgress()}%`}}
        >
          <span>
            <b>{Math.round(calculateReviewedProgress())}%</b> corrigido
          </span>
        </div>
      </section>
    </Modal>
  );
}

export default CommentsLoadingModal;
